// src/components/prestige/RulesZones.js
import React, { useState } from "react";
import {
  Settings,
  AlertTriangle,
  CheckCircle,
  Clock,
  MapPin,
  ChevronDown,
  ChevronRight,
  Filter,
  TrendingUp,
  TrendingDown,
  Bell,
  BellOff,
  Edit,
  Save,
  Zap,
  AlertOctagon,
  Eye,
  BarChart3,
} from "lucide-react";
import { Modal, Select, Input, message } from "antd";
import axios from "../../utils/axiosDash";

const { Option } = Select;

// ============================================================
// LABEL COMPONENT
// ============================================================

const Label = ({ children, className = "" }) => (
  <span
    className={`text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500 ${className}`}
  >
    {children}
  </span>
);

// ============================================================
// STATUS HELPERS
// ============================================================

const getHealthStatus = (health) => {
  const statusMap = {
    Good: {
      color: "text-emerald-700",
      bg: "bg-emerald-50",
      border: "border-emerald-200",
      icon: CheckCircle,
    },
    Acceptable: {
      color: "text-amber-700",
      bg: "bg-amber-50",
      border: "border-amber-200",
      icon: AlertTriangle,
    },
    "Over-firing": {
      color: "text-red-700",
      bg: "bg-red-50",
      border: "border-red-200",
      icon: AlertOctagon,
    },
    "Review samples": {
      color: "text-blue-700",
      bg: "bg-blue-50",
      border: "border-blue-200",
      icon: Eye,
    },
  };

  return statusMap[health] || {
    color: "text-blue-700",
    bg: "bg-blue-50",
    border: "border-blue-200",
    icon: Eye,
  };
};

const getSeverityConfig = (severity) => {
  const configs = {
    Critical: {
      color: "text-red-700",
      bg: "bg-red-50",
      border: "border-red-200",
      icon: AlertOctagon,
    },
    High: {
      color: "text-amber-700",
      bg: "bg-amber-50",
      border: "border-amber-200",
      icon: AlertTriangle,
    },
    Medium: {
      color: "text-blue-700",
      bg: "bg-blue-50",
      border: "border-blue-200",
      icon: Bell,
    },
  };

  return configs[severity] || configs.Medium;
};

// ============================================================
// DUMMY DATA
// ============================================================

const initialRules = [
  {
    id: 1,
    rule: "Crowd density",
    zone: "B10 GF Reception",
    threshold: "≥ 3.0 /m² for 60 s",
    schedule: "Always",
    fires: 14,
    falseRate: 7,
    health: "Good",
    camera: "B10_GF_RECP-1",
    type: "overcrowding",
  },
  {
    id: 2,
    rule: "Crowd density",
    zone: "B20 GF Main Lobby",
    threshold: "≥ 3.0 /m² for 60 s",
    schedule: "Always",
    fires: 18,
    falseRate: 12,
    health: "Good",
    camera: "B20_GF_PLL1",
    type: "overcrowding",
  },
  {
    id: 3,
    rule: "Crowd density",
    zone: "B30 GF Cafeteria",
    threshold: "≥ 3.0 /m² for 60 s",
    schedule: "Always",
    fires: 11,
    falseRate: 9,
    health: "Good",
    camera: "B30_GF_RECP1",
    type: "overcrowding",
  },
  {
    id: 4,
    rule: "Fall detection",
    zone: "All common areas",
    threshold: "Posture + 5 s still",
    schedule: "Always",
    fires: 9,
    falseRate: 22,
    health: "Acceptable",
    camera: "Multiple",
    type: "fall",
  },
  {
    id: 5,
    rule: "Altercation",
    zone: "B10 Lobby, Food court",
    threshold: "Aggression score ≥ 0.8",
    schedule: "Always",
    fires: 6,
    falseRate: 17,
    health: "Good",
    camera: "B10_GF_LIFT-1, B20_GF_PLL1",
    type: "altercation",
  },
  {
    id: 6,
    rule: "Unattended object",
    zone: "B10 GF Reception, B20 GF Lobby",
    threshold: "Object alone > 5 min",
    schedule: "Always",
    fires: 31,
    falseRate: 35,
    health: "Raise to 8 min",
    camera: "B10_GF_RECP-1, B20_GF_PLL1",
    type: "unattended_object",
  },
  {
    id: 7,
    rule: "Loitering",
    zone: "Main Gate approach",
    threshold: "> 8 min in zone",
    schedule: "Always",
    fires: 212,
    falseRate: 86,
    health: "Over-firing",
    camera: "OUTSIDE-1, OUTSIDE-2",
    type: "loitering",
  },
  {
    id: 8,
    rule: "Loitering",
    zone: "MLCP Parking stairwells",
    threshold: "> 10 min in zone",
    schedule: "Always",
    fires: 27,
    falseRate: 30,
    health: "Acceptable",
    camera: "MLCP_GF_STAIRCASE-1, MLCP_1F_STAIRCASE-3",
    type: "loitering",
  },
  {
    id: 9,
    rule: "Restricted entry",
    zone: "B10 Server rooms, BMS, roof",
    threshold: "Tripwire crossing",
    schedule: "Always",
    fires: 8,
    falseRate: 12,
    health: "Good",
    camera: "B10_GF_ELEC_RM-1, B10_TERRACE_STAIRCASE-1",
    type: "restricted_entry",
  },
  {
    id: 10,
    rule: "Restricted entry",
    zone: "B10 TT Outside",
    threshold: "Zone entry",
    schedule: "22:00-06:00",
    fires: 48,
    falseRate: 61,
    health: "Whitelist night crew",
    camera: "B10_TT_OUTSIDE-1, B10_TT_OUTSIDE-2",
    type: "restricted_entry",
  },
  {
    id: 11,
    rule: "Suspicious behaviour",
    zone: "MLCP Parking P1-P2",
    threshold: "Anomaly score ≥ 0.85",
    schedule: "Always",
    fires: 19,
    falseRate: 42,
    health: "Review samples",
    camera: "MLCP_B1_PLL, MLCP_B2_PLL",
    type: "suspicious",
  },
];

const escalationMatrix = [
  {
    severity: "Critical",
    notify: "SOC + patrol lead + FM head",
    channels: "Dashboard",
    escalate: "60 s → security head",
  },
  {
    severity: "High",
    notify: "SOC + patrol lead",
    channels: "Dashboard",
    escalate: "3 min → FM head",
  },
  {
    severity: "Medium",
    notify: "SOC",
    channels: "Dashboard",
    escalate: "15 min → patrol lead",
  },
];

const suggestedTuning = [
  {
    rule: "Gate 2 loitering",
    suggestion: "→ 20 min, exclude 06-09",
    impact: "High",
    estimatedReduction: 58,
  },
  {
    rule: "Dock: whitelist night crew",
    suggestion: "→ Whitelist night crew badges",
    impact: "Medium",
    estimatedReduction: null,
  },
  {
    rule: "Unattended object",
    suggestion: "5 → 8 min",
    impact: "Medium",
    estimatedReduction: null,
  },
  {
    rule: "P1-P2 anomaly",
    suggestion: "≥ 0.9",
    impact: "Low",
    estimatedReduction: null,
  },
];

// ============================================================
// REUSABLE UI COMPONENTS
// ============================================================

const SectionHeader = ({
  eyebrow,
  title,
  description,
  icon: Icon,
  iconClassName = "text-violet-600",
  iconBackground = "bg-violet-50",
  rightContent,
}) => (
  <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
    <div className="flex min-w-0 items-start gap-3">
      {Icon && (
        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${iconBackground}`}
        >
          <Icon size={18} className={iconClassName} />
        </div>
      )}

      <div className="min-w-0">
        {eyebrow && <Label>{eyebrow}</Label>}
        <h2 className="mt-1 text-sm font-bold text-slate-950">{title}</h2>
        {description && (
          <p className="mt-1 text-xs text-slate-500">{description}</p>
        )}
      </div>
    </div>

    {rightContent}
  </div>
);

const StatCard = ({ label, value, description, icon: Icon, tone }) => {
  const tones = {
    violet: {
      iconBackground: "bg-violet-50",
      iconColor: "text-violet-600",
    },
    blue: {
      iconBackground: "bg-blue-50",
      iconColor: "text-blue-600",
    },
    amber: {
      iconBackground: "bg-amber-50",
      iconColor: "text-amber-600",
    },
    red: {
      iconBackground: "bg-red-50",
      iconColor: "text-red-600",
    },
  };

  const selectedTone = tones[tone] || tones.violet;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <Label>{label}</Label>
          <p className="mt-2 text-3xl font-bold tracking-tight text-slate-950">
            {value}
          </p>
          <p className="mt-1 text-xs text-slate-500">{description}</p>
        </div>

        <div
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${selectedTone.iconBackground}`}
        >
          <Icon size={19} className={selectedTone.iconColor} />
        </div>
      </div>
    </div>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================

const RulesZones = () => {
  const [rules, setRules] = useState(initialRules);
  const [selectedRule, setSelectedRule] = useState(null);
  const [expandedRule, setExpandedRule] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [filterType, setFilterType] = useState("All");
  const [filterHealth, setFilterHealth] = useState("All");
  const [showTuningModal, setShowTuningModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const filteredRules = rules.filter((rule) => {
    if (filterType !== "All" && rule.rule !== filterType) return false;
    if (filterHealth !== "All" && rule.health !== filterHealth) return false;
    return true;
  });

  const handleEditRule = (rule) => {
    setSelectedRule(rule);
    setEditForm({ ...rule });
    setIsEditing(true);
  };

  const handleSaveRule = () => {
    setRules(rules.map((r) => (r.id === editForm.id ? editForm : r)));
    setIsEditing(false);
    setSelectedRule(null);
    message.success("Rule updated successfully");
  };

  const handleToggleRule = (ruleId) => {
    setRules(
      rules.map((rule) =>
        rule.id === ruleId
          ? { ...rule, enabled: !rule.enabled }
          : rule
      )
    );
  };

  const totalRules = rules.length;
  const healthyRules = rules.filter((rule) => rule.health === "Good").length;
  const overFiringRules = rules.filter(
    (rule) => rule.health === "Over-firing"
  ).length;
  const totalFires = rules.reduce((total, rule) => total + rule.fires, 0);

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-5 sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-[1600px] flex-col gap-6">
        {/* Header */}
        <header className="flex flex-wrap items-end justify-between gap-5">
          <div>
            <div className="mb-2 flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-violet-600" />
              <Label className="text-violet-600">Command 3</Label>
            </div>

            <h1 className="text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">
              Rules & Zones
            </h1>

            <p className="mt-2 max-w-2xl text-sm text-slate-500">
              Configure detection rules, monitor alert quality, and manage
              zone-level security response policies.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => setShowTuningModal(true)}
              className="inline-flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm font-semibold text-amber-700 transition-colors hover:bg-amber-100"
            >
              <Zap size={16} />
              Suggested Tuning
            </button>

            <button
              onClick={() => {
                setLoading(true);
                setTimeout(() => setLoading(false), 1000);
              }}
              className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-violet-700"
            >
              <Settings size={16} />
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </header>

        {/* Summary Cards */}
        <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            label="Active Rules"
            value={totalRules}
            description="Configured detection rules"
            icon={Settings}
            tone="violet"
          />

          <StatCard
            label="Healthy Rules"
            value={healthyRules}
            description="Rules operating normally"
            icon={CheckCircle}
            tone="blue"
          />

          <StatCard
            label="Total Fires"
            value={totalFires}
            description="Alerts recorded in 30 days"
            icon={Bell}
            tone="amber"
          />

          <StatCard
            label="Over-Firing Rules"
            value={overFiringRules}
            description="Rules requiring attention"
            icon={AlertTriangle}
            tone="red"
          />
        </section>

        {/* Filters */}
        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100">
                <Filter size={14} className="text-slate-500" />
              </div>

              <span className="text-xs font-semibold text-slate-700">
                Filter rules
              </span>
            </div>

            <Select
              value={filterType}
              onChange={setFilterType}
              className="w-[170px]"
              size="middle"
            >
              <Option value="All">All Rules</Option>
              <Option value="Crowd density">Crowd density</Option>
              <Option value="Fall detection">Fall detection</Option>
              <Option value="Altercation">Altercation</Option>
              <Option value="Unattended object">Unattended object</Option>
              <Option value="Loitering">Loitering</Option>
              <Option value="Restricted entry">Restricted entry</Option>
              <Option value="Suspicious behaviour">
                Suspicious behaviour
              </Option>
            </Select>

            <Select
              value={filterHealth}
              onChange={setFilterHealth}
              className="w-[170px]"
              size="middle"
            >
              <Option value="All">All Health</Option>
              <Option value="Good">Good</Option>
              <Option value="Acceptable">Acceptable</Option>
              <Option value="Over-firing">Over-firing</Option>
              <Option value="Review samples">Review Samples</Option>
            </Select>

            <div className="ml-auto rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600">
              {filteredRules.length} rules
            </div>
          </div>
        </section>

        {/* Rules Table */}
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <SectionHeader
            eyebrow="Detection configuration"
            title="Rules by zone"
            description="Review thresholds, schedules, alert quality, and current rule health"
            icon={Settings}
            rightContent={
              <div className="flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[10px] font-bold text-emerald-700">
                <span className="h-2 w-2 rounded-full bg-emerald-500" />
                MONITORING ACTIVE
              </div>
            }
          />

          <div className="overflow-x-auto">
            <table className="w-full min-w-[1050px] border-collapse">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Rule
                  </th>
                  <th className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Zone
                  </th>
                  <th className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Threshold
                  </th>
                  <th className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Schedule
                  </th>
                  <th className="px-5 py-3 text-center text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Fires
                  </th>
                  <th className="px-5 py-3 text-center text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    False %
                  </th>
                  <th className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Health
                  </th>
                  <th className="px-5 py-3 text-center text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {filteredRules.map((rule) => {
                  const healthStatus = getHealthStatus(rule.health);
                  const HealthIcon = healthStatus.icon;
                  const isExpanded = expandedRule === rule.id;

                  return (
                    <React.Fragment key={rule.id}>
                      <tr
                        className={`cursor-pointer border-b border-slate-100 transition-colors hover:bg-slate-50 ${
                          isExpanded ? "bg-slate-50" : "bg-white"
                        }`}
                        onClick={() =>
                          setExpandedRule(isExpanded ? null : rule.id)
                        }
                      >
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-slate-900">
                              {rule.rule}
                            </span>

                            {isExpanded ? (
                              <ChevronDown
                                size={14}
                                className="text-slate-400"
                              />
                            ) : (
                              <ChevronRight
                                size={14}
                                className="text-slate-400"
                              />
                            )}
                          </div>
                        </td>

                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2">
                            <MapPin
                              size={13}
                              className="shrink-0 text-violet-500"
                            />
                            <span className="text-xs text-slate-700">
                              {rule.zone}
                            </span>
                          </div>
                        </td>

                        <td className="px-5 py-4">
                          <span className="rounded-md bg-slate-100 px-2 py-1 font-mono text-[11px] text-slate-600">
                            {rule.threshold}
                          </span>
                        </td>

                        <td className="px-5 py-4">
                          <div className="flex items-center gap-1.5 text-xs text-slate-600">
                            <Clock size={13} className="text-slate-400" />
                            {rule.schedule}
                          </div>
                        </td>

                        <td className="px-5 py-4 text-center">
                          <span className="text-xs font-bold text-slate-900">
                            {rule.fires}
                          </span>
                        </td>

                        <td className="px-5 py-4 text-center">
                          <span
                            className={`text-xs font-bold ${
                              rule.falseRate > 50
                                ? "text-red-600"
                                : rule.falseRate > 30
                                ? "text-amber-600"
                                : "text-emerald-600"
                            }`}
                          >
                            {rule.falseRate}%
                          </span>
                        </td>

                        <td className="px-5 py-4">
                          <span
                            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-bold ${healthStatus.bg} ${healthStatus.color} ${healthStatus.border}`}
                          >
                            <HealthIcon size={11} />
                            {rule.health}
                          </span>
                        </td>

                        <td className="px-5 py-4 text-center">
                          <div className="flex items-center justify-center gap-1.5">
                            <button
                              onClick={(event) => {
                                event.stopPropagation();
                                handleEditRule(rule);
                              }}
                              className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-blue-50 hover:text-blue-600"
                              title="Edit rule"
                            >
                              <Edit size={14} />
                            </button>

                            <button
                              onClick={(event) => {
                                event.stopPropagation();
                                handleToggleRule(rule.id);
                              }}
                              className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100"
                              title={
                                rule.enabled !== false
                                  ? "Disable rule"
                                  : "Enable rule"
                              }
                            >
                              {rule.enabled !== false ? (
                                <Bell
                                  size={14}
                                  className="text-emerald-500"
                                />
                              ) : (
                                <BellOff
                                  size={14}
                                  className="text-slate-300"
                                />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr>
                          <td
                            colSpan="8"
                            className="border-b border-slate-100 bg-slate-50 px-5 py-4"
                          >
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                              <div className="rounded-xl border border-slate-200 bg-white p-4">
                                <Label className="text-slate-400">
                                  Camera
                                </Label>
                                <p className="mt-2 text-xs font-semibold text-slate-700">
                                  {rule.camera}
                                </p>
                              </div>

                              <div className="rounded-xl border border-slate-200 bg-white p-4">
                                <Label className="text-slate-400">
                                  Rule Type
                                </Label>
                                <p className="mt-2 text-xs font-semibold text-slate-700">
                                  {rule.type}
                                </p>
                              </div>

                              <div className="rounded-xl border border-slate-200 bg-white p-4">
                                <Label className="text-slate-400">
                                  Current Status
                                </Label>

                                <div className="mt-2 flex flex-wrap items-center gap-2">
                                  <span
                                    className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-bold ${healthStatus.bg} ${healthStatus.color}`}
                                  >
                                    <HealthIcon size={11} />
                                    {rule.health}
                                  </span>

                                  <span className="text-xs text-slate-500">
                                    {rule.fires} alerts · {rule.falseRate}%
                                    false
                                  </span>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        {/* Suggested Tuning */}
        <section className="overflow-hidden rounded-2xl border border-amber-200 bg-amber-50 shadow-sm">
          <SectionHeader
            eyebrow="Optimization recommendations"
            title="Suggested tuning"
            description="Recommended changes to reduce alert noise"
            icon={Zap}
            iconClassName="text-amber-600"
            iconBackground="bg-amber-100"
            rightContent={
              <span className="rounded-full border border-amber-200 bg-white px-3 py-1.5 text-[10px] font-bold text-amber-700">
                ACTIONABLE
              </span>
            }
          />

          <div className="p-5">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {suggestedTuning.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white p-4 transition-shadow hover:shadow-sm"
                >
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-slate-900">
                      {item.rule}
                    </p>
                    <p className="mt-1 text-[11px] text-slate-500">
                      {item.suggestion}
                    </p>
                  </div>

                  {item.estimatedReduction && (
                    <div className="flex shrink-0 items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1.5">
                      <TrendingDown
                        size={12}
                        className="text-emerald-600"
                      />
                      <span className="text-[10px] font-bold text-emerald-700">
                        -{item.estimatedReduction}%
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-4 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-100/60 p-4">
              <TrendingDown
                size={16}
                className="mt-0.5 shrink-0 text-amber-700"
              />

              <p className="text-xs leading-5 text-amber-800">
                <span className="font-bold">
                  Applying the top two changes removes an estimated 58% of
                  false alarms
                </span>{" "}
                while keeping every confirmed incident from the last 30 days
                detectable.
              </p>
            </div>
          </div>
        </section>

        {/* Escalation Matrix */}
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <SectionHeader
            eyebrow="Response protocol"
            title="Escalation matrix"
            description="Alert severity, notification channels, and escalation timing"
            icon={BarChart3}
            iconClassName="text-violet-600"
            iconBackground="bg-violet-50"
          />

          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Severity
                  </th>
                  <th className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Notify
                  </th>
                  <th className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Channels
                  </th>
                  <th className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Escalate if silent
                  </th>
                </tr>
              </thead>

              <tbody>
                {escalationMatrix.map((item, index) => {
                  const severity = getSeverityConfig(item.severity);
                  const SeverityIcon = severity.icon;

                  return (
                    <tr
                      key={index}
                      className="border-b border-slate-100 transition-colors last:border-0 hover:bg-slate-50"
                    >
                      <td className="px-5 py-4">
                        <span
                          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-bold ${severity.bg} ${severity.color} ${severity.border}`}
                        >
                          <SeverityIcon size={11} />
                          {item.severity}
                        </span>
                      </td>

                      <td className="px-5 py-4 text-xs text-slate-700">
                        {item.notify}
                      </td>

                      <td className="px-5 py-4 text-xs text-slate-700">
                        {item.channels}
                      </td>

                      <td className="px-5 py-4">
                        <span className="text-xs font-semibold text-amber-600">
                          {item.escalate}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        {/* Edit Rule Modal */}
        <Modal
          open={isEditing}
          onCancel={() => {
            setIsEditing(false);
            setSelectedRule(null);
          }}
          footer={null}
          title={
            <div className="flex items-center gap-2">
              <Settings size={16} className="text-violet-600" />
              <span className="text-sm font-semibold">Edit Rule</span>
            </div>
          }
          width={600}
        >
          {editForm && (
            <div className="space-y-4 pt-2">
              <div>
                <Label>Rule Name</Label>
                <Input
                  value={editForm.rule}
                  onChange={(event) =>
                    setEditForm({
                      ...editForm,
                      rule: event.target.value,
                    })
                  }
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Zone</Label>
                <Input
                  value={editForm.zone}
                  onChange={(event) =>
                    setEditForm({
                      ...editForm,
                      zone: event.target.value,
                    })
                  }
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Threshold</Label>
                <Input
                  value={editForm.threshold}
                  onChange={(event) =>
                    setEditForm({
                      ...editForm,
                      threshold: event.target.value,
                    })
                  }
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Schedule</Label>
                <Input
                  value={editForm.schedule}
                  onChange={(event) =>
                    setEditForm({
                      ...editForm,
                      schedule: event.target.value,
                    })
                  }
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Health Status</Label>
                <Select
                  value={editForm.health}
                  onChange={(value) =>
                    setEditForm({
                      ...editForm,
                      health: value,
                    })
                  }
                  className="mt-1 w-full"
                >
                  <Option value="Good">Good</Option>
                  <Option value="Acceptable">Acceptable</Option>
                  <Option value="Over-firing">Over-firing</Option>
                  <Option value="Review samples">Review samples</Option>
                  <Option value="Raise to 8 min">Raise to 8 min</Option>
                  <Option value="Whitelist night crew">
                    Whitelist night crew
                  </Option>
                </Select>
              </div>

              <div>
                <Label>Camera</Label>
                <Input
                  value={editForm.camera}
                  onChange={(event) =>
                    setEditForm({
                      ...editForm,
                      camera: event.target.value,
                    })
                  }
                  className="mt-1"
                />
              </div>

              <div className="flex items-center justify-end gap-3 border-t border-slate-100 pt-4">
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setSelectedRule(null);
                  }}
                  className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50"
                >
                  Cancel
                </button>

                <button
                  onClick={handleSaveRule}
                  className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-violet-700"
                >
                  <Save size={14} />
                  Save Changes
                </button>
              </div>
            </div>
          )}
        </Modal>

        {/* Suggested Tuning Modal */}
        <Modal
          open={showTuningModal}
          onCancel={() => setShowTuningModal(false)}
          footer={null}
          title={
            <div className="flex items-center gap-2">
              <Zap size={16} className="text-amber-600" />
              <span className="text-sm font-semibold">
                Suggested Tuning
              </span>
            </div>
          }
          width={650}
        >
          <div className="space-y-4 pt-2">
            <p className="text-sm leading-6 text-slate-600">
              Review the recommended rule changes below to reduce false alarms
              and improve detection quality.
            </p>

            <div className="space-y-3">
              {suggestedTuning.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 bg-slate-50 p-4"
                >
                  <div>
                    <p className="text-sm font-bold text-slate-900">
                      {item.rule}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      {item.suggestion}
                    </p>
                  </div>

                  {item.estimatedReduction ? (
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-bold text-emerald-700">
                      <TrendingDown size={12} />
                      -{item.estimatedReduction}%
                    </span>
                  ) : (
                    <span className="rounded-full bg-slate-200 px-2.5 py-1 text-[10px] font-bold text-slate-600">
                      Review
                    </span>
                  )}
                </div>
              ))}
            </div>

            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <p className="text-xs leading-5 text-amber-800">
                <span className="font-bold">Recommendation:</span> Apply the
                top two tuning changes first and monitor alert quality for the
                next 30 days.
              </p>
            </div>

            <div className="flex justify-end border-t border-slate-100 pt-4">
              <button
                onClick={() => setShowTuningModal(false)}
                className="rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-violet-700"
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default RulesZones;